import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { Bell, ScanLine, Ticket, X } from 'lucide-react';
import Icon from 'components/_UI/Icon';
import APP_STORES from 'constants/appStores';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';

const BENEFITS = [
    { icon: Ticket, text: 'Seus ingressos sempre à mão' },
    { icon: ScanLine, text: 'Check-in mais rápido na entrada' },
    { icon: Bell, text: 'Avisos dos eventos que você curte' },
];

const STORES = {
    ios: { label: 'Baixar na App Store', logo: AppleLogo },
    android: { label: 'Baixar no Google Play', logo: PlayLogo },
};

/**
 * Sheet de download do app. Só apresentação: quando abrir e o que lembrar
 * fica no AppDownloadPrompt.
 */
export default function AppDownloadSheet({ isOpen, platform, onDismiss, onStoreClick }) {
    const sheetRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return undefined;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        sheetRef.current?.focus();

        function onKeyDown(event) {
            if (event.key === 'Escape') onDismiss();
        }

        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen, onDismiss]);

    if (!isOpen || !platform) return null;

    const store = STORES[platform];
    const StoreLogo = store.logo;

    return createPortal(
        <>
            <Overlay onClick={onDismiss} />
            <Sheet
                ref={sheetRef}
                role='dialog'
                aria-modal='true'
                aria-labelledby='app-download-title'
                aria-describedby='app-download-lead'
                tabIndex={-1}
            >
                <Hero>
                    <Glow aria-hidden='true' />
                    <Handle aria-hidden='true' />

                    <CloseButton type='button' onClick={onDismiss} aria-label='Fechar'>
                        <X size={20} strokeWidth={2.25} />
                    </CloseButton>

                    <Logo aria-hidden='true'>
                        <Icon name='whizr-inline-logo' color={colors.neutralLight} />
                    </Logo>

                    <Title id='app-download-title'>Whizr é melhor no app</Title>
                    <Lead id='app-download-lead'>
                        Tudo dos seus eventos num toque, sem precisar abrir o navegador.
                    </Lead>
                </Hero>

                <Body>
                    <Benefits>
                        {BENEFITS.map(({ icon: BenefitIcon, text }) => (
                            <Benefit key={text}>
                                <BenefitIconWrap aria-hidden='true'>
                                    <BenefitIcon size={18} strokeWidth={2.25} />
                                </BenefitIconWrap>
                                {text}
                            </Benefit>
                        ))}
                    </Benefits>

                    <StoreButton
                        href={APP_STORES[platform]}
                        target='_blank'
                        rel='noopener noreferrer'
                        onClick={() => onStoreClick(platform)}
                    >
                        <StoreLogo />
                        {store.label}
                    </StoreButton>

                    <ContinueButton type='button' onClick={onDismiss}>
                        Continuar no site
                    </ContinueButton>
                </Body>
            </Sheet>
        </>,
        document.body
    );
}

function AppleLogo() {
    return (
        <svg width='18' height='18' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
            <path d='M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701' />
        </svg>
    );
}

function PlayLogo() {
    return (
        <svg width='17' height='17' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
            <path d='M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z' />
        </svg>
    );
}

const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
`;

const slideUp = keyframes`
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
`;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 400;
    background: rgba(18, 15, 47, 0.55);
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    animation: ${fadeIn} .25s ease-out;
`;

const Sheet = styled.div`
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 401;
    max-height: 92vh;
    overflow-y: auto;
    background: ${colors.neutralLight};
    border-radius: 24px 24px 0 0;
    box-shadow: 0 -12px 40px rgba(18, 15, 47, 0.25);
    animation: ${slideUp} .36s cubic-bezier(0.32, 0.72, 0, 1);
    outline: none;

    @media screen and (min-width: ${breakpoints.smd}) {
        left: 50%;
        right: auto;
        width: 420px;
        margin-left: -210px;
    }

    @media (prefers-reduced-motion: reduce) {
        animation: ${fadeIn} .2s ease-out;
    }
`;

const Hero = styled.div`
    position: relative;
    overflow: hidden;
    padding: 28px 24px 26px;
    text-align: center;
    color: ${colors.neutralLight};
    background: linear-gradient(150deg, ${colors.secondary} 0%, #2A1670 55%, ${colors.homeAccent} 100%);
`;

const Glow = styled.span`
    position: absolute;
    top: -90px;
    right: -70px;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(194, 156, 255, 0.45) 0%, rgba(194, 156, 255, 0) 70%);
    pointer-events: none;
`;

const Handle = styled.span`
    position: absolute;
    top: 8px;
    left: 50%;
    width: 40px;
    height: 4px;
    margin-left: -20px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.35);
`;

const CloseButton = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.14);
    color: ${colors.neutralLight};
    cursor: pointer;
    transition: background .2s;

    &:hover { background: rgba(255, 255, 255, 0.24); }

    &:focus-visible {
        outline: 2px solid ${colors.neutralLight};
        outline-offset: 2px;
    }
`;

const Logo = styled.div`
    position: relative;
    width: 108px;
    height: 30px;
    margin: 10px auto 18px;
`;

const Title = styled.h2`
    position: relative;
    margin: 0 0 8px;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: -0.3px;
`;

const Lead = styled.p`
    position: relative;
    max-width: 300px;
    margin: 0 auto;
    font-size: 15px;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.85);
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 22px 24px calc(20px + env(safe-area-inset-bottom));
`;

const Benefits = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 0 0 10px;
    padding: 0;
    list-style: none;
`;

const Benefit = styled.li`
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 15px;
    font-weight: 500;
    color: ${colors.nightDark};
`;

const BenefitIconWrap = styled.span`
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: #F1E9FE;
    color: ${colors.homeAccent};
`;

const StoreButton = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 52px;
    padding: 0 20px;
    border-radius: 14px;
    background: ${colors.homeAccent};
    color: ${colors.neutralLight};
    font-size: 16px;
    font-weight: 600;
    text-decoration: none;
    box-shadow: 0 8px 20px rgba(105, 12, 228, 0.3);
    transition: transform .15s, box-shadow .2s, background .2s;

    &:hover {
        background: #5A0AC4;
        box-shadow: 0 10px 24px rgba(105, 12, 228, 0.38);
    }

    &:active { transform: scale(0.98); }

    &:focus-visible {
        outline: 3px solid #C29CFF;
        outline-offset: 2px;
    }
`;

const ContinueButton = styled.button`
    min-height: 44px;
    border: none;
    border-radius: 12px;
    background: none;
    color: ${colors.coralGrey};
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background .2s, color .2s;

    &:hover {
        background: ${colors.neutral};
        color: ${colors.nightDark};
    }

    &:focus-visible {
        outline: 2px solid ${colors.homeAccent};
        outline-offset: 2px;
    }
`;
